"use client";

import type { UseFormReturn } from "react-hook-form";
import { AiReplyAssistToolbar, useAiReplyAssist, type AiReplyContext } from "@wtc/ai-compose";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import type { EmailTemplate } from "@/lib/hooks/api/emails/email-templates";
import type { ComposeFormValues, EmailConnection } from "@/lib/types/compose-modal-types";
import type { ComposeClientContextValue } from "@/lib/core/crm/types";
import { TemplateSelector } from "../TemplateSelector";
import { UnifiedBodyField } from "./UnifiedBodyField";

interface ComposeBodyFieldProps {
  clientState: ComposeClientContextValue;
  emailConnections?: EmailConnection[];
  form: UseFormReturn<ComposeFormValues>;
  onAttachmentsChange: (attachments?: ComposeFormValues["attachments"]) => void;
  onTemplateSelect?: (template: EmailTemplate) => void;
  /** True when composing a reply (drives the AI assist context). */
  isReply?: boolean;
}

/** Split recipient string ("a@x.com, b@y.com") into trimmed addresses. */
function splitRecipients(value: string | undefined): string[] {
  return (value ?? "")
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function ComposeBodyField({
  clientState,
  emailConnections,
  form,
  onAttachmentsChange,
  onTemplateSelect,
  isReply,
}: ComposeBodyFieldProps) {
  const { run, isLoading, error } = useAiReplyAssist();

  return (
    <FormField
      control={form.control}
      name="body"
      render={({ field }) => {
        const senderEmail = (emailConnections || []).find(
          (conn) => conn.connectionId === form.watch("fromAccount")
        )?.email;

        const buildContext = (): AiReplyContext => {
          const recipientEmails = splitRecipients(form.watch("to"));
          return {
            subject: form.watch("subject") || undefined,
            recipientEmails: recipientEmails.length > 0 ? recipientEmails : undefined,
            senderName: senderEmail,
            isReply: Boolean(isReply),
          };
        };

        const handleRun = async (
          intent: Parameters<typeof run>[0],
          instruction?: string
        ): Promise<void> => {
          const text = await run(intent, {
            currentBody: field.value || "",
            instruction,
            context: buildContext(),
          });
          if (text) field.onChange(text);
        };

        return (
          <FormItem>
            <div className="flex items-center justify-between gap-2">
              <FormLabel>Body</FormLabel>
              <TemplateSelector onSelect={onTemplateSelect || (() => {})} className="w-auto" />
            </div>
            <AiReplyAssistToolbar
              hasBody={Boolean((field.value || "").trim())}
              isLoading={isLoading}
              error={error}
              onRun={handleRun}
              className="mb-1"
            />
            <FormControl>
              <UnifiedBodyField
                value={field.value}
                onChange={field.onChange}
                placeholder="Compose your email..."
                className="min-h-[250px] bg-background"
                selectedClient={clientState.selectedClient}
                email={senderEmail}
                isSecondAccount={false}
                onAttachmentsChange={onAttachmentsChange}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
